import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, RefreshCw, Droplets } from "lucide-react";
import { toast } from "sonner";
import { Reveal, StockBadge } from "@/components/Motion";
import { API } from "@/data/site";

const fmt = (cents) => `$${(cents / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;

export default function Product() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState("loading");
  const [usdRate, setUsdRate] = useState(null);
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/products/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((p) => {
        setProduct(p);
        setStatus("ok");
      })
      .catch(() => setStatus("missing"));
  }, [slug]);

  useEffect(() => {
    fetch(`${API}/api/fx`)
      .then((r) => r.json())
      .then((d) => setUsdRate(d.rate))
      .catch(() => {});
  }, []);

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await fetch(`${API}/api/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, quantity: qty, origin_url: window.location.origin }),
      }).then(async (r) => ({ data: await r.json(), ok: r.ok, status: r.status }));
      if (!data.checkout_url) {
        toast.error(data.detail || "Checkout is not available yet.");
      } else {
        window.location.assign(data.checkout_url);
        return;
      }
    } catch {
      toast.error("Checkout is not available yet. Please use the contact form for orders.");
    } finally {
      setBuying(false);
    }
  };

  if (status === "loading")
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-24">
        <p className="font-lab text-xs uppercase tracking-[0.18em] text-[#56636b]">Loading product…</p>
      </div>
    );

  if (status === "missing")
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-5 pt-24 text-center">
        <p className="font-display text-4xl">Product not found</p>
        <Link to="/" className="border border-[#0a0a0a] px-6 py-3 text-sm" data-testid="product-notfound-link">
          Back to the collection
        </Link>
      </div>
    );

  const usd = usdRate
    ? (product.price_cents / 100 * usdRate).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;
  const specs = [
    ["Size", product.size],
    ["Exosome count", product.potency],
    ["Application", product.application],
    ["Format", product.form],
    ["SKU", product.sku],
    ["Collection", product.collection],
  ];

  return (
    <div className="pt-16" data-testid="product-detail">
      <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-10 md:py-14">
        <Link
          to="/"
          data-testid="product-back-link"
          className="font-lab inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[#56636b] transition-colors hover:text-[#0a0a0a]"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {product.collection}
        </Link>

        <div className="mt-10 grid grid-cols-1 gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="group relative overflow-hidden border border-[#e4e9ec] bg-[#f4f3ef]">
              <img
                src={product.image}
                alt={product.name}
                data-testid="product-detail-image"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <span className="font-lab absolute left-4 top-4 bg-white/85 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] backdrop-blur-sm">
                {product.category}
              </span>
            </div>
          </Reveal>

          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">{product.collection}</p>
              <h1 className="font-display mt-3 text-4xl leading-tight tracking-tight md:text-5xl" data-testid="product-detail-name">
                {product.name}
              </h1>
              <p className="mt-3 text-base text-[#56636b]">{product.tagline}</p>
              <p
                className="font-lab mt-4 inline-flex items-center gap-2 border border-[#0e8c8c]/30 bg-[#e3f2f0]/60 px-3.5 py-2 text-[11px] uppercase tracking-[0.16em] text-[#0e8c8c]"
                data-testid="product-potency"
              >
                <Droplets className="h-3.5 w-3.5" /> {product.potency} exosomes
              </p>

              <div className="mt-6">
                <StockBadge available={product.available} />
              </div>

              <div className="mt-8 flex items-end gap-4 border-y border-[#e4e9ec] py-6">
                <div>
                  <span className="font-display text-4xl" data-testid="price-cad">
                    {fmt(product.price_cents)} <span className="font-lab align-middle text-xs text-[#56636b]">CAD</span>
                  </span>
                  <p className="font-lab mt-1.5 text-[10px] uppercase tracking-[0.14em] text-[#56636b]" data-testid="price-label">
                    Suggested retail price
                  </p>
                </div>
                {usd && (
                  <span className="font-lab pb-1.5 text-xs text-[#56636b]" data-testid="price-usd">
                    ≈ ${usd} USD · live rate
                  </span>
                )}
              </div>

              <div className="mt-6 flex items-center gap-4" data-testid="product-purchase-row">
                <select
                  value={qty}
                  onChange={(e) => setQty(Number(e.target.value))}
                  data-testid="quantity-select"
                  className="border border-[#e4e9ec] bg-white px-4 py-3.5 text-sm outline-none transition-colors focus:border-[#0a0a0a]"
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>Qty {n}</option>
                  ))}
                </select>
                <button
                  onClick={buy}
                  disabled={buying || product.available === 0}
                  data-testid="buy-square-button"
                  className="group flex-1 bg-[#0a0a0a] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#0e8c8c] disabled:opacity-40"
                >
                  {product.available === 0 ? "Out of stock" : buying ? "Opening secure checkout…" : "Buy now"}
                </button>
              </div>

              <p className="font-lab mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[#56636b]">
                <ShieldCheck className="h-3.5 w-3.5 text-[#0e8c8c]" /> Secure checkout · CAD charged · USD approximate
              </p>

              <div className="mt-6 border border-[#0e8c8c]/25 bg-[#e3f2f0]/40 p-5" data-testid="distributor-note">
                <p className="font-lab text-[10px] uppercase tracking-[0.16em] text-[#0e8c8c]">For distributors</p>
                <p className="mt-2 text-[13px] leading-relaxed text-[#0a0a0a]">
                  Connect with your local representative to place your order. If you do not have an assigned rep,{" "}
                  <Link to="/#partner" data-testid="distributor-note-link" className="font-medium text-[#0e8c8c] underline underline-offset-4">
                    fill out our contact form
                  </Link>{" "}
                  and someone will connect with you shortly.
                </p>
              </div>

              <p className="mt-8 text-[15px] leading-relaxed text-[#0a0a0a]">{product.description}</p>

              <div className="mt-8 border border-[#e4e9ec]">
                <p className="font-lab border-b border-[#e4e9ec] px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-[#56636b]">
                  Specifications
                </p>
                <div className="grid grid-cols-2">
                  {specs.map(([k, v], i) => (
                    <div
                      key={k}
                      className={`px-4 py-3 ${i % 2 === 0 ? "border-r border-[#e4e9ec]" : ""} ${i < specs.length - 2 ? "border-b border-[#e4e9ec]" : ""}`}
                      data-testid={`product-spec-${k.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <p className="font-lab text-[9px] uppercase tracking-[0.14em] text-[#56636b]">{k}</p>
                      <p className="mt-1 text-sm">{v}</p>
                    </div>
                  ))}
                </div>
              </div>

              <p className="font-lab mt-6 flex items-start gap-2 text-[11px] leading-relaxed text-[#56636b]" data-testid="product-ingredients-note">
                <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0e8c8c]" /> {product.ingredients_note}
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}